import { useState, useEffect, useCallback } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  CheckCircle, AlertTriangle, XCircle, RefreshCw, Clock, Activity,
  Server, Database, Cpu, Globe,
} from "lucide-react";
import { Logo1895Icon } from "@/components/Logo1895";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// ── Helpers ────────────────────────────────────────────────────────────────

function statusMeta(status) {
  switch (status) {
    case "operational":
      return { color: "#22c55e", label: "Operational",    icon: CheckCircle,   bg: "rgba(34,197,94,0.1)"  };
    case "degraded":
      return { color: "#f59e0b", label: "Degraded",       icon: AlertTriangle, bg: "rgba(245,158,11,0.1)" };
    case "outage":
      return { color: "#ef4444", label: "Outage",         icon: XCircle,       bg: "rgba(239,68,68,0.1)"  };
    case "partial_outage":
      return { color: "#f59e0b", label: "Partial Outage", icon: AlertTriangle, bg: "rgba(245,158,11,0.1)" };
    case "major_outage":
      return { color: "#ef4444", label: "Major Outage",   icon: XCircle,       bg: "rgba(239,68,68,0.1)"  };
    default:
      return { color: "#6b7280", label: "Unknown",        icon: RefreshCw,     bg: "rgba(107,114,128,0.1)" };
  }
}

function overallMeta(status) {
  switch (status) {
    case "operational":
      return { color: "#22c55e", bg: "rgba(34,197,94,0.08)",   border: "rgba(34,197,94,0.25)",   label: "All Systems Operational" };
    case "partial_outage":
      return { color: "#f59e0b", bg: "rgba(245,158,11,0.08)",  border: "rgba(245,158,11,0.25)",  label: "Partial Outage" };
    case "major_outage":
      return { color: "#ef4444", bg: "rgba(239,68,68,0.08)",   border: "rgba(239,68,68,0.25)",   label: "Major Outage" };
    default:
      return { color: "#6b7280", bg: "rgba(107,114,128,0.08)", border: "rgba(107,114,128,0.25)", label: "Checking..." };
  }
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function formatHour(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function timeAgo(date) {
  if (!date) return "";
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}

const CHART_SERVICES = [
  { id: "api",       name: "API",       color: "#d4a84b" },
  { id: "database",  name: "Database",  color: "#22c55e" },
  { id: "ai_claude", name: "AI Claude", color: "#a78bfa" },
  { id: "ai_gemini", name: "AI Gemini", color: "#38bdf8" },
];

// Service grouping configuration
const SERVICE_GROUPS = {
  core: {
    label: "Core",
    icon: Server,
    ids: ["api", "database"],
  },
  features: {
    label: "Features",
    icon: Cpu,
    ids: ["scripts", "characters", "storyboard", "locations", "analysis", "greenlight", "scenes", "shots"],
  },
  external: {
    label: "External — AI Services",
    icon: Globe,
    ids: ["ai_claude", "ai_gemini"],
  },
};

function groupServices(services) {
  const groups = {};
  const assigned = new Set();

  for (const [key, group] of Object.entries(SERVICE_GROUPS)) {
    const matched = services.filter(s => group.ids.includes(s.id));
    if (matched.length > 0) {
      groups[key] = { ...group, services: matched };
      matched.forEach(s => assigned.add(s.id));
    }
  }

  // Catch any services not assigned to a group
  const ungrouped = services.filter(s => !assigned.has(s.id));
  if (ungrouped.length > 0) {
    if (groups.features) {
      groups.features.services.push(...ungrouped);
    } else {
      groups.other = { label: "Other", icon: Server, services: ungrouped };
    }
  }

  return groups;
}

// ── Sub-components ─────────────────────────────────────────────────────────

function ServiceCard({ service }) {
  const meta = statusMeta(service.status);
  const Icon = meta.icon;
  return (
    <div style={{
      background: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,255,255,0.07)",
      borderRadius: 12,
      padding: "14px 16px",
      display: "flex",
      alignItems: "center",
      gap: 12,
      transition: "border-color 0.2s",
    }}>
      {/* Status dot */}
      <div style={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        background: meta.color,
        boxShadow: `0 0 8px ${meta.color}40`,
        flexShrink: 0,
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary, #f5f3ef)", marginBottom: 2 }}>
          {service.name}
        </div>
        <div style={{ fontSize: 11, color: meta.color, fontWeight: 500 }}>
          {meta.label}
        </div>
      </div>
      {service.latency_ms > 0 && (
        <div style={{
          fontSize: 12,
          color: "rgba(255,255,255,0.5)",
          fontFamily: "'JetBrains Mono', monospace",
          flexShrink: 0,
          background: "rgba(255,255,255,0.04)",
          padding: "3px 8px",
          borderRadius: 6,
        }}>
          {service.latency_ms}ms
        </div>
      )}
    </div>
  );
}

function ServiceGroup({ group }) {
  const GroupIcon = group.icon;
  return (
    <div style={{ marginBottom: 32 }}>
      <h3 style={{
        fontSize: 11,
        fontWeight: 700,
        color: "rgba(255,255,255,0.35)",
        textTransform: "uppercase",
        letterSpacing: "0.1em",
        marginBottom: 12,
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}>
        <GroupIcon size={13} />
        {group.label}
      </h3>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
        gap: 10,
      }}>
        {group.services.map(svc => (
          <ServiceCard key={svc.id} service={svc} />
        ))}
      </div>
    </div>
  );
}

function UptimeRow({ entry }) {
  const badge7d  = entry.uptime_7d  >= 99.9 ? "#22c55e" : entry.uptime_7d  >= 99 ? "#f59e0b" : "#ef4444";
  const badge30d = entry.uptime_30d >= 99.9 ? "#22c55e" : entry.uptime_30d >= 99 ? "#f59e0b" : "#ef4444";
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      padding: "10px 0",
      borderBottom: "1px solid rgba(255,255,255,0.05)",
      gap: 12,
    }}>
      <div style={{ flex: 1, fontSize: 13, color: "rgba(255,255,255,0.8)" }}>{entry.name}</div>
      <div style={{ fontSize: 12, color: badge7d, fontFamily: "'JetBrains Mono', monospace", minWidth: 56, textAlign: "right" }}>
        {entry.uptime_7d.toFixed(2)}%
      </div>
      <div style={{ fontSize: 12, color: badge30d, fontFamily: "'JetBrains Mono', monospace", minWidth: 56, textAlign: "right" }}>
        {entry.uptime_30d.toFixed(2)}%
      </div>
    </div>
  );
}

function IncidentRow({ incident }) {
  const meta = overallMeta(incident.status);
  return (
    <div style={{
      display: "flex",
      gap: 14,
      padding: "12px 0",
      borderBottom: "1px solid rgba(255,255,255,0.05)",
    }}>
      <div style={{
        width: 8, height: 8, borderRadius: "50%",
        background: meta.color,
        marginTop: 5, flexShrink: 0,
      }} />
      <div>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.8)", marginBottom: 4 }}>
          <span style={{ color: meta.color, fontWeight: 600 }}>{meta.label}</span>
          {incident.affected?.length > 0 && (
            <span style={{ color: "rgba(255,255,255,0.45)", fontSize: 12 }}>
              {" — "}{incident.affected.join(", ")}
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", display: "flex", alignItems: "center", gap: 4 }}>
          <Clock size={10} />
          {formatTime(incident.timestamp)}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

export default function StatusPage() {
  const [full, setFull]       = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [, setTick] = useState(0); // force re-render for "X seconds ago"

  const fetchData = useCallback(async () => {
    try {
      const [fullRes, histRes] = await Promise.all([
        fetch(`${API_BASE}/api/health/full`),
        fetch(`${API_BASE}/api/health/history`),
      ]);
      const fullJson = await fullRes.json();
      const histJson = await histRes.json();
      setFull(fullJson);
      setHistory(histJson);
      setLastRefresh(new Date());
    } catch (err) {
      console.error("Status page fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const dataInterval = setInterval(fetchData, 60_000);
    // Tick every 10s to update "X seconds ago" display
    const tickInterval = setInterval(() => setTick(t => t + 1), 10_000);
    return () => {
      clearInterval(dataInterval);
      clearInterval(tickInterval);
    };
  }, [fetchData]);

  const overall = overallMeta(full?.status);
  const OverallIcon = full?.status === "operational" ? CheckCircle
    : full?.status === "major_outage" ? XCircle
    : AlertTriangle;

  const groups = groupServices(full?.services || []);

  return (
    <div style={{
      minHeight: "100vh",
      background: "#030712",
      color: "#f5f3ef",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      {/* Header */}
      <header style={{
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        padding: "20px 0",
      }}>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 24px", display: "flex", alignItems: "center", gap: 16 }}>
          <a href="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
            <Logo1895Icon width={28} />
            <span style={{ fontSize: 16, fontWeight: 600, color: "#f5f3ef" }}>1895.ai</span>
          </a>
          <span style={{ color: "rgba(255,255,255,0.2)" }}>/</span>
          <span style={{ fontSize: 14, color: "rgba(255,255,255,0.5)" }}>System Status</span>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
            {lastRefresh && (
              <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", display: "flex", alignItems: "center", gap: 4 }}>
                <Activity size={11} />
                Updated {timeAgo(lastRefresh)}
              </span>
            )}
            <button
              onClick={fetchData}
              style={{
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 8,
                padding: "6px 12px",
                color: "rgba(255,255,255,0.6)",
                fontSize: 12,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <RefreshCw size={12} />
              Refresh
            </button>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 900, margin: "0 auto", padding: "40px 24px 80px" }}>

        {/* Overall banner */}
        {loading ? (
          <div style={{
            background: "rgba(107,114,128,0.08)",
            border: "1px solid rgba(107,114,128,0.25)",
            borderRadius: 16,
            padding: "36px 32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            marginBottom: 48,
          }}>
            <RefreshCw size={24} color="#6b7280" style={{ animation: "spin 1s linear infinite" }} />
            <span style={{ fontSize: 18, fontWeight: 600, color: "#6b7280" }}>Checking system status...</span>
          </div>
        ) : (
          <div style={{
            background: overall.bg,
            border: `1px solid ${overall.border}`,
            borderRadius: 16,
            padding: "28px 32px",
            display: "flex",
            alignItems: "center",
            gap: 16,
            marginBottom: 48,
          }}>
            <OverallIcon size={32} color={overall.color} />
            <div>
              <div style={{ fontSize: 22, fontWeight: 700, color: overall.color, letterSpacing: "-0.02em" }}>
                {overall.label}
              </div>
              {full?.checked_at && (
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginTop: 4 }}>
                  Last checked: {formatTime(full.checked_at)}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Grouped service grid */}
        {!loading && (
          <section style={{ marginBottom: 16 }}>
            {Object.entries(groups).map(([key, group]) => (
              <ServiceGroup key={key} group={group} />
            ))}
            {/* Fallback if no groups matched */}
            {Object.keys(groups).length === 0 && (full?.services || []).length > 0 && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: 10,
              }}>
                {(full?.services || []).map(svc => (
                  <ServiceCard key={svc.id} service={svc} />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Response time chart */}
        <section style={{ marginBottom: 48 }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 16 }}>
            Response Time — Last 24h
          </h2>
          <div style={{
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 16,
            padding: "24px 16px 16px",
          }}>
            {(!history?.last_24h || history.last_24h.length === 0) ? (
              <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 13, textAlign: "center", padding: "40px 0" }}>
                No history data yet — data accumulates as the health checker runs.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={history.last_24h} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis
                    dataKey="time"
                    tickFormatter={formatHour}
                    tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 11 }}
                    axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    unit="ms"
                  />
                  <Tooltip
                    contentStyle={{
                      background: "#0a0908",
                      border: "1px solid rgba(255,255,255,0.12)",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    labelStyle={{ color: "rgba(255,255,255,0.5)" }}
                    formatter={(val, name) => [`${val}ms`, name]}
                    labelFormatter={formatHour}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}
                  />
                  {CHART_SERVICES.map(s => (
                    <Line
                      key={s.id}
                      type="monotone"
                      dataKey={s.id}
                      name={s.name}
                      stroke={s.color}
                      strokeWidth={1.5}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>

        {/* Uptime table */}
        <section style={{ marginBottom: 48 }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 16 }}>
            Uptime
          </h2>
          <div style={{
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 16,
            padding: "0 24px",
          }}>
            {/* Header */}
            <div style={{
              display: "flex",
              padding: "12px 0",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
            }}>
              <div style={{ flex: 1, fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Service</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.08em", minWidth: 56, textAlign: "right" }}>7 days</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.08em", minWidth: 56, textAlign: "right", marginLeft: 12 }}>30 days</div>
            </div>
            {(history?.uptime || []).map(entry => (
              <UptimeRow key={entry.id} entry={entry} />
            ))}
            {(!history?.uptime || history.uptime.length === 0) && (
              <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 13, padding: "24px 0", textAlign: "center" }}>
                No uptime data yet.
              </div>
            )}
          </div>
        </section>

        {/* Incident timeline */}
        <section>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 16 }}>
            Incident History — Last 30 Days
          </h2>
          <div style={{
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 16,
            padding: "0 24px",
          }}>
            {(!history?.incidents || history.incidents.length === 0) ? (
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "28px 0",
                color: "#22c55e",
              }}>
                <CheckCircle size={16} />
                <span style={{ fontSize: 14 }}>No incidents in the last 30 days.</span>
              </div>
            ) : (
              history.incidents.map((inc, i) => (
                <IncidentRow key={i} incident={inc} />
              ))
            )}
          </div>
        </section>

      </main>

      {/* Footer */}
      <footer style={{
        borderTop: "1px solid rgba(255,255,255,0.06)",
        padding: "20px 24px",
        textAlign: "center",
        fontSize: 12,
        color: "rgba(255,255,255,0.25)",
      }}>
        {lastRefresh && (
          <span>Last checked: {timeAgo(lastRefresh)} &nbsp;&middot;&nbsp; </span>
        )}
        Auto-refreshes every 60 seconds &nbsp;&middot;&nbsp; 1895.ai
      </footer>

      {/* Keyframe for spinner */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
